import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { ExpandMore } from '@mui/icons-material';
import { Checkbox, FormControlLabel, Menu, MenuItem, Typography } from '@mui/material';
import { INextStep } from '@modules/ContactManager/components/Header/interfaces';
import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import {
  refreshWorkflow,
  setLastCommentsRefresh,
  setLastUpdateProductsPlanDate,
  setRefreshFiles,
  setRefreshWorkflow,
  setWorkflowNextStep,
} from '@store/reducers/contactManagerSlice';
import { formatCurrency, formatDate, formatPercentage, openPdf, queryParams, withQueryParams } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { Permissions, Roles } from '@shared/constants/constants';
import { LabelText } from '@styles/StyledTypography';
import { DealsButton } from '@styles/StyledButtons';
import {
  DealBuilderActions,
  DealBuilderBody,
  DealBuilderBodySection,
  DealBuilderHeader,
  DealBuilderLabel,
} from '@styles/StyledDiv';
import { getError } from '@shared/utils/helpers';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { useRefreshComments } from '@shared/hooks/useRefreshComments';
import { useTabs } from '@shared/hooks/useTabs';
import { IAsset, IOpenLendingApplicationResponse } from '../../Assets/interfaces';
import { IDealBuilderProps, IPushSubprimeLenderData, ISubprimeResponse, SubprimeType } from '../interfaces';
import {
  addDealComment,
  requestBuyBack,
  requestCopyDeal,
  requestCreateSubprimeDeal,
  requestFlexCertificateMisMatch,
  requestHotTitle,
  requestPushSubprimeLenders,
} from '../queries';
import { DelaysModal } from './DelaysModal';
import { DocsMenu } from './DocsMenu/DocsMenu';
import { DocuSignAlert } from './DocsMenu/DocuSignAlert';
import { DOCS_MENU, DOC_TYPES, IDocsMenu, IDocumentGenerate } from './DocsMenu/interface';
import { TitleAlert } from './DocsMenu/TitleAlert';
import { PushButtons } from './PushButtons';
import {
  requestDocumentGenerate,
  requestDocumentValidate,
  requestDocumentValidateEnvelope,
  requestDocumentVoid,
  requestDocuSign,
  requestToGeneratePdf,
} from './DocsMenu/queries';
import { SubprimeResponseModal } from './SubprimeResponseModal';
import { FlexCertificateModal } from './FlexCertificateModal';
import { CustomDocsModal } from './DocsMenu/CustomDocs/CustomDocsModal';
import { OL_CODES } from './constants';

const autoResponder = 'AutoResponder';

export const DealBuilder = ({ data, deals, setFieldError, refreshDealsTable }: IDealBuilderProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const params = queryParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openTab, updateCurrentTab } = useTabs();
  const isDocGenTitle = userHasRoles([Roles.DOC_GEN_TITLE]);
  const isDocGenLender = userHasRoles([Roles.DOC_GEN_LENDER]);
  const isAccounting = userHasRoles([Roles.ACCOUNING]);
  const mutateDealComment = useRefreshComments(params.dealId);
  const isOpenLendingCertificate = userHasRoles([Roles.OPENLENDINGCERTIFICATE, Roles.ADMIN]);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [openDelaysModal, setOpenDelaysModal] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subprimeAnchorEl, setSubprimeAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  // Docs menu variables
  const [loaderText, setLoaderText] = useState<string>();
  const [isOpenDocModal, setIsOpenDocModal] = useState(0);
  const [docMenuItem, setDocMenuItem] = useState<IDocsMenu | null>(null);
  const [openDocuSignAlert, setOpenDocuSignAlert] = useState(false);
  const [openTitleAlert, setOpenTitleAlert] = useState(false);
  const [openProductsAlert, setOpenProductsAlert] = useState(false);
  const [openEnvelopeAlert, setOpenEnvelopeAlert] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flexModalData, setFlexModelData] = useState('');
  const [subprimeAlert, setSubprimeAlert] = useState({ open: false, title: '', keyword: '', loading: false });
  const [subprimeRespModal, setSubprimeRespModal] = useState<{ open: boolean; data: ISubprimeResponse[] }>({
    open: false,
    data: [],
  });
  const [hotTitles, setHotTitles] = useState(data?.isHotTitles || false);
  const [buyBacks, setBuyBacks] = useState(data?.isBuyBack || false);
  const [validationError, setValidationError] = useState('');
  const isSubprimeEnable = isAdmin || userHasRoles([Permissions.SUBPRIME]);

  const [filteredDocTypeIds, setFilteredDocTypeIds] = useState<string[]>([]);
  const [showSalePriceAllert, setShowSalePriceAllert] = useState(false);  

  const isAutoResponderDeal = useMemo(() => data?.currentWorkflowStatus?.includes(autoResponder), [data?.currentWorkflowStatus]);

  useEffect(() => {
    setHotTitles(!!data?.isHotTitles);
    setBuyBacks(!!data?.isBuyBack);
  }, [data]);

  const contact = useSWR<NContact.Entity>(`/${SERVICES.CONTACT}/contacts/${params.id}`);
  const { data: asset } = useSWR<IAsset>(data?.assetId && `${SERVICES.ASSET}/assets/${data?.assetId}`);
  const openLending = useSWR<IOpenLendingApplicationResponse>(`/${SERVICES.LENDER}/open-lending/deal/${params.dealId}`);

  const refreshWorkflowSection = useSelector(refreshWorkflow);

  const handleCopyDeal = useCallback(async () => {
    setCopyDialogOpen(false);
    setLoading(true);
    const resp: { id: string } = await requestCopyDeal(params.dealId, params.contactId);
    navigate(withQueryParams('/contact-manager', { dealId: resp.id }));
    refreshDealsTable && (await refreshDealsTable());
    setLoading(false);
  }, [requestCopyDeal, refreshDealsTable, params]);

  const openDealSheet = useCallback(() => {
    const title = `${contact?.data?.firstName} ${contact?.data?.lastName}-Deal Sheet`;
    const route = withQueryParams('/deal-sheet', { id: contact?.data?.id });
    updateCurrentTab({ title, route });
    navigate(route);
  }, [contact]);

  const handleStipReview = useCallback(() => {
    const title = `${contact?.data?.firstName} ${contact?.data?.lastName}-STIPS`;
    const route = withQueryParams('/stips-review-tab', { id: params.id, dealId: params.dealId });
    openTab({ title, route });
    navigate(route);
  }, [contact]);

  const handleFlexCertificate = useCallback(async () => {
    setLoading(true);
    const openLendingApp = openLending?.data;

    if (openLendingApp) {
      const response = await requestFlexCertificateMisMatch(openLendingApp.id, openLendingApp.dealId);

      if (response && response.html) {
        setFlexModelData(response.html);
        setIsModalOpen(true);
      }
      setLoading(false);
    }
  }, [openLending]);

  const handleDocsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const salesPrice = data?.financeConditionItem?.salesPrice;
    const lienAmount = data?.dealAssetResponse?.lienAmount;
    if (salesPrice !== lienAmount) {
      setShowSalePriceAllert(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onPdfGenerate = useCallback(
    async (dealId: string, documentGenerationType: number, item: IDocsMenu | null) => {
      const text =
        item?.docTypeId === DOC_TYPES.TITLE_DOCS
          ? 'Deal saved, generating title documents...'
          : 'Deal saved, generating documents...';

      setLoading(true);
      setLoaderText(text);
      const content = await requestToGeneratePdf(dealId, documentGenerationType);
      if (content) {
        openPdf(content.document, content.fileName);
        dispatch(setRefreshWorkflow(refreshWorkflowSection));
        dispatch(setWorkflowNextStep({} as INextStep));
      }

      if (item?.docuSign) {
        if (item.isCustom && data?.coContact) {
          setOpenDocuSignAlert(true);
        } else {
          const data = {
            dealId,
            isCustomDocuSign: item.isCustom ?? false,
            documentGenerationType,
            shouldSendToCoBorrowerForCustomDocuSign: false,
            requiresDroppedDocuSign: true,
          };
          setLoading(true);
          setLoaderText('Sending to DocuSign...');
          await requestDocuSign(data);
          setLoading(false);
          setDocMenuItem(null);
        }
      }
      
      dispatch(setLastUpdateProductsPlanDate(new Date().toISOString()));
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      dispatch(setRefreshFiles(new Date().toISOString()));
      setLoaderText('');
      setLoading(false);
    },
    [docMenuItem, requestToGeneratePdf, data?.coContact, openPdf, refreshWorkflowSection]
  );

  const onDocGenerate = useCallback(
    async (docsData: IDocumentGenerate, item: IDocsMenu) => {
      setLoading(true);
      const resp = await requestDocumentGenerate(docsData);

      if (resp === '200') {
        onPdfGenerate(docsData.dealId, docsData.documentGenerationType, item);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // TODO: Fix logic below, because it is incorrect
      } else if (resp?.includes('Error Contracting Products' && item.docTypeId === DOC_TYPES.LENDER && !item.isCustom)) {
        setOpenProductsAlert(true);
        setLoading(false);
      } else {
        dispatch(setLastUpdateProductsPlanDate(new Date().toISOString()));
        setDocMenuItem(null);
        setLoading(false);
      }
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
    },
    [requestDocumentGenerate, onPdfGenerate, docMenuItem]
  );

  const onValidation = useCallback(
    async (docsData: IDocumentGenerate, item: IDocsMenu) => {
      setLoading(true);
      const resp = await requestDocumentValidate(params.dealId);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (resp?.data) {
        if (item.docTypeId === DOC_TYPES.DROPPED_DOC) {
          onDocGenerate(docsData, item);
        } else {
          setLoading(true);
          const respEnvlp = await requestDocumentValidateEnvelope(params.dealId);
          setLoading(false);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (respEnvlp) {
            onDocGenerate(docsData, item);
          } else {
            setOpenEnvelopeAlert(true);
          }
        }
      } else {
        setLoading(false);
        setDocMenuItem(null);
      }
    },
    [requestDocumentValidate, params.dealId, onDocGenerate]
  );

  const handleDoc = useCallback(
    (docsData: IDocumentGenerate, item: IDocsMenu, docModalSubmitted: boolean) => {
      const docuSignIds = [DOCS_MENU['Doc Gen & Docu Sign'], DOCS_MENU['Dropped Doc Gen & Docu Sign']];
      const droppedDocGen =
        item.docTypeId === DOC_TYPES.DROPPED_DOC && item.docuSign
          ? data?.droppedCoContact && data?.droppedCoContact?.email
          : true;
      if (contact.data?.firstName && contact.data?.lastName && asset?.vin && (!item.docuSign || droppedDocGen)) {
        if (item.isCustom && docModalSubmitted) {
          setIsOpenDocModal((prevState) => prevState + 1);
        } else if (item.docTypeId === DOC_TYPES.TITLE_DOCS) {
          if (data?.assetId) {
            setFilteredDocTypeIds(docsData.filterDocumentTypeIds);
            setOpenTitleAlert(true);
          } else {
            Notistack.generalError('No assets found for this deal');
          }
        } else if (docuSignIds.includes(item.id)) {
          // Only for Docu Sign, not for Custom-Doc Gen and Docu Sign
          onValidation(docsData, item);
        } else {
          onDocGenerate(docsData, item);
        }
      } else if (
        item.docuSign &&
        item.docTypeId === DOC_TYPES.DROPPED_DOC &&
        data?.droppedCoContact &&
        !data.droppedCoContact?.email
      ) {
        setValidationError('Error generating docs: Missing Dropped Borrower Email');
      } else {
        let error = '';
        error += (!contact.data?.firstName && '"FirstName" must not be empty \n') || '';
        error += (!contact.data?.lastName && '"LastName" must not be empty \n') || '';
        error += (!asset?.vin && '"Vin" must not be empty') || '';
        error = !error ? 'Product Submit Contract Error' : error;
        Notistack.generalError(error);
      }
    },
    [contact.data, asset, data?.assetId, onDocGenerate, onValidation]
  );

  const handleProductsConfirm = useCallback(async () => {
    const docsData: IDocumentGenerate = {
      dealId: params.dealId,
      documentGenerationType: DOC_TYPES.LENDER,
      ignoreProducts: true,
      isEmailCustomerTitleDocGen: false,
      isScanStipTitleDocGen: false,
      filterDocumentTypeIds: [],
    };

    setLoading(true);
    setOpenProductsAlert(false);
    const res = await requestDocumentGenerate({ ...docsData, ignoreProducts: true });

    if (res === '200') {
      await onPdfGenerate(docsData.dealId, docsData.documentGenerationType, docMenuItem);
    } else {
      setLoading(false);
      setDocMenuItem(null);
    }
  }, [params.dealId, requestDocumentGenerate, onPdfGenerate, docMenuItem]);

  const handleEnevolpeConfirm = useCallback(async () => {
    const docsData: IDocumentGenerate = {
      dealId: params.dealId,
      documentGenerationType: DOC_TYPES.LENDER,
      ignoreProducts: false,
      isEmailCustomerTitleDocGen: false,
      isScanStipTitleDocGen: false,
      filterDocumentTypeIds: [],
    };

    setLoading(true);
    setOpenEnvelopeAlert(false);
    const resp = await requestDocumentVoid(params.dealId);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (resp?.data) {
      onDocGenerate(docsData, docMenuItem as IDocsMenu);
    } else {
      setDocMenuItem(null);
      setLoading(false);
    }
  }, [onDocGenerate, requestDocumentVoid, docMenuItem, params.dealId]);

  const onDocsMenuItemClick = useCallback(
    (item: IDocsMenu) => {
      const ids = [
        DOCS_MENU['Doc Gen & Docu Sign'],
        DOCS_MENU['Custom-Doc Gen & Docu Sign'],
        DOCS_MENU['Dropped Doc Gen & Docu Sign'],
      ];

      if (ids.includes(item.id)) {
        if (!data?.cudlAccountNumber && (data?.lenderResponse?.lenderCode !== 'QLO' || !data?.lenderResponse?.isQuilo)) {
          Notistack.generalError('Please input APP # before DocGen');
          setFieldError('cudlAccountNumber', ' ');
          handleClose();
          return;
        }
      }
      setFieldError('cudlAccountNumber', '');
      setDocMenuItem(item);
      const docObject: IDocumentGenerate = {
        dealId: params.dealId,
        documentGenerationType: item.docTypeId,
        ignoreProducts: ![DOC_TYPES.LENDER, DOC_TYPES.DROPPED_DOC].includes(item.docTypeId),
        isEmailCustomerTitleDocGen: false,
        isScanStipTitleDocGen: false,
        filterDocumentTypeIds: [],
      };

      handleClose();
      handleDoc(docObject, item, true);
    },
    [handleClose, handleDoc, data, params.dealId]
  );

  const handleSubprimeMenu = useCallback(
    (menuItem: string) => {
      const title = `Would you like to ${
        menuItem === SubprimeType.createDeals ? 'create' : 'submit'
      } deal: ${data?.numericId} to Subprime?`;
      setSubprimeAlert((prevState) => ({ ...prevState, open: true, title, keyword: menuItem }));
    },
    [data?.numericId]
  );

  const handleSubprimeSubmit = useCallback(async () => {
    setSubprimeAlert((prevState) => ({ ...prevState, loading: true }));

    if (data) {
      let response: ISubprimeResponse[] | undefined;
      try {
        if (subprimeAlert.keyword === SubprimeType.createDeals) {
          response = await requestCreateSubprimeDeal(data?.contact?.id, data?.leadId, data?.id);
        } else {
          const payload: IPushSubprimeLenderData[] = [];

          if (deals) {
            deals?.forEach((pushToLenderDeal) => {
              if (pushToLenderDeal.leadId === data?.leadId && pushToLenderDeal.currentWorkflowStatus === 'DRAFT DEAL') {
                payload.push({
                  dealId: pushToLenderDeal?.dealId,
                  dealNumericId: pushToLenderDeal?.dealNumericId,
                  leadId: pushToLenderDeal?.leadId,
                  lenderCode: pushToLenderDeal?.lenderCode,
                  lenderName: pushToLenderDeal?.lenderName,
                  overrideHughesData: false,
                  overrideData: false,
                  originalLoanAmount: 0,
                  comment: 'Pushed To Lender',
                });
              }
            });
          }
          response = await requestPushSubprimeLenders(payload);

          if (response?.length && response.filter((r) => r.isSuccess).length > 0) {
            const comment = `Subprime pushed to lenders ${response.filter((r) => r.isSuccess).map((r) => r.lenderName)}`;
            await addDealComment(data.id, { comment, dealId: data.id, commentActionType: 8 });
          }
        }
        //reload deals and comments
        refreshDealsTable?.();
        mutateDealComment();
        setSubprimeRespModal((prevState) => ({ ...prevState, open: true, data: response || [] }));
      } catch (err) {
        Notistack.generalError(getError(err));
      }
    }
    setSubprimeAlert((prevState) => ({ ...prevState, loading: false, open: false }));
  }, [data, subprimeAlert]);

  const handleChangeHotTitle = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true);
      const isHotTitles = event.target.checked;
      setHotTitles(isHotTitles);

      await requestHotTitle(params.dealId, isHotTitles);
      setLoading(false);
    },
    [requestHotTitle, params.dealId]
  );

  const handleChangeBuyBack = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true);
      const isBuyBack = event.target.checked;
      setBuyBacks(isBuyBack);

      await requestBuyBack(params.dealId, isBuyBack);
      setLoading(false);
    },
    [requestBuyBack, params.dealId]
  );

  return (
    <div>
      <LoaderBackdrop
        open={loading}
        loaderText={loaderText}
      />
      <DealBuilderHeader>
        {setShowSalePriceAllert && (
            <AlertModal
                hideCancel
                open={showSalePriceAllert}
                title="Alert"
                subtitle="Asset Lien Amount is not equal to Sales Price"
                buttonText="Ok"
                onClose={() => setShowSalePriceAllert(false)}
                onConfirm={() => setShowSalePriceAllert(false)}
              />
        )}
        <Typography
          variant="h3"
          whiteSpace="nowrap"
          mb={0}
        >
          Deal Builder {data?.numericId}
        </Typography>
        {subprimeAlert?.open && (
          <AlertModal
            title="Subprime deal"
            isLoading={subprimeAlert.loading}
            subtitle={subprimeAlert.title}
            open={subprimeAlert.open}
            onConfirm={handleSubprimeSubmit}
            onClose={() => setSubprimeAlert((prevState) => ({ ...prevState, open: false }))}
          />
        )}
        {subprimeRespModal.open && (
          <SubprimeResponseModal
            open={subprimeRespModal.open}
            data={subprimeRespModal.data}
            modalFor={subprimeAlert.keyword}
            onClose={() => setSubprimeRespModal((prevState) => ({ ...prevState, open: false }))}
          />
        )}
        {copyDialogOpen && (
          <AlertModal
            title="Copy deal"
            subtitle="Are you sure you want to copy this deal?"
            open={copyDialogOpen}
            onConfirm={handleCopyDeal}
            onClose={() => setCopyDialogOpen(false)}
          />
        )}
        {!!openDelaysModal && (
          <DelaysModal
            openDelayModalPosition={openDelaysModal}
            handleClose={() => setOpenDelaysModal(0)}
          />
        )}

        <DealBuilderActions>
          <DealsButton
            disabled={isAutoResponderDeal}
            onClick={() => setCopyDialogOpen(true)}
          >
            Copy
          </DealsButton>
          <PushButtons
            data={data}
            refreshDealsTable={refreshDealsTable}
          />
          {isSubprimeEnable && (
            <>
              <DealsButton
                endIcon={<ExpandMore />}
                onClick={(e) => setSubprimeAnchorEl(e.currentTarget)}
              >
                Subprime
              </DealsButton>

              <Menu
                id="basic-menu"
                anchorEl={subprimeAnchorEl}
                open={!!subprimeAnchorEl}
                MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                onClose={() => setSubprimeAnchorEl(null)}
              >
                <MenuItem onClick={() => handleSubprimeMenu('createDeals')}>Create Deals</MenuItem>
                <MenuItem onClick={() => handleSubprimeMenu('pushToLenders')}>Push to Lenders</MenuItem>
              </Menu>
            </>
          )}
          {(isAdmin || isAccounting) && <DealsButton onClick={openDealSheet}>Deal Sheet</DealsButton>}
          {(isAdmin || isDocGenTitle || isDocGenLender) && (
            <>
              <DealsButton
                endIcon={<ExpandMore />}
                onClick={handleDocsClick}
              >
                Docs
              </DealsButton>

              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={!!anchorEl}
                MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                onClose={handleClose}
              >
                <DocsMenu
                  allowDroppedDocGenAndDocuSign={data?.allowDroppedDocGenAndDocuSign}
                  handleMenuItemClick={onDocsMenuItemClick}
                />
              </Menu>
            </>
          )}
          {docMenuItem && (
            <>
              {!!isOpenDocModal && (
                <CustomDocsModal
                  customDocModalPosition={isOpenDocModal}
                  dealId={params.dealId}
                  handleDoc={handleDoc}
                  docMenuItem={docMenuItem}
                  onClose={() => setIsOpenDocModal(0)}
                  />
              )}

              {openDocuSignAlert && (
                <DocuSignAlert
                  openDocuSignAlert={openDocuSignAlert}
                  setOpenDocuSignAlert={setOpenDocuSignAlert}
                  setLoading={setLoading}
                  setLoaderText={setLoaderText}
                  documentGeneratedType={docMenuItem?.docTypeId}
                  dealId={params.dealId}
                  customDocuSign={docMenuItem.isCustom ?? false}
                />
              )}

              {openTitleAlert && (
                <TitleAlert
                  openTitleAlert={openTitleAlert}
                  setOpenTitleAlert={setOpenTitleAlert}
                  assetId={data?.assetId}
                  docMenuItem={docMenuItem}
                  contact={contact.data}
                  filterDocumentTypeIds={filteredDocTypeIds}
                  onDocGenerate={onDocGenerate}
                />
              )}

              {openProductsAlert && (
                <AlertModal
                  open={openProductsAlert}
                  title="Ignore Products"
                  subtitle="Error while contracting the products , Do you want to ignore products and Generate docs?"
                  buttonText="Yes"
                  cancelButtonText="No"
                  onConfirm={handleProductsConfirm}
                  onClose={() => {
                    setOpenProductsAlert(false);
                    setDocMenuItem(null);
                  }}
                />
              )}

              {openEnvelopeAlert && (
                <AlertModal
                  open={openEnvelopeAlert}
                  title="Envelope exists."
                  buttonText="Continue"
                  subtitle={`Do you want to resend the envelope for deal ${
                    data?.numericId ?? ''
                  }. This will void and create a new envelope`}
                  onConfirm={handleEnevolpeConfirm}
                  onClose={() => {
                    setOpenEnvelopeAlert(false);
                    setDocMenuItem(null);
                  }}
                />
              )}

              {validationError && (
                <AlertModal
                  open
                  hideCancel
                  title="Alert"
                  subtitle={validationError}
                  buttonText="Ok"
                  onConfirm={() => setValidationError('')}
                  onClose={() => setValidationError('')}
                />
              )}
            </>
          )}
          <DealsButton onClick={() => setOpenDelaysModal((prevState) => prevState + 1)}>Delays</DealsButton>
          <DealsButton
            onClick={() => {
              handleStipReview();
            }}
          >
            STIPS Review
          </DealsButton>
          {data?.lenderResponse?.isOpenLending && isOpenLendingCertificate && (
            <>
              <DealsButton onClick={handleFlexCertificate}>Flex Certificate</DealsButton>
              <FlexCertificateModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                html={flexModalData}
                isOFCUOL={data?.lenderResponse.isOpenLending && data?.lenderResponse?.lenderCode === OL_CODES.OFCUOL}
              />
            </>
          )}
          <FormControlLabel
            label={<LabelText>Hot Title</LabelText>}
            control={
              <Checkbox
                name="isHotTitles"
                checked={hotTitles}
                onChange={handleChangeHotTitle}
              />
            }
          />
          <FormControlLabel
            label={<LabelText>Buy Back</LabelText>}
            control={
              <Checkbox
                name="isBuyBack"
                checked={buyBacks}
                onChange={handleChangeBuyBack}
              />
            }
          />
        </DealBuilderActions>
      </DealBuilderHeader>

      <DealBuilderBody>
        <DealBuilderBodySection>
          <DealBuilderLabel>Rate</DealBuilderLabel> {formatPercentage(data?.financeConditionItem?.rate || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Term</DealBuilderLabel> {data?.financeConditionItem?.term || 0}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Sales Price</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.salesPrice || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Fees</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.feesTotal || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Tax</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.totalSalesTaxAmount || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Front-End</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.frontEndTotal || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Total</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.totalAmountFinanced || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>APR</DealBuilderLabel> {formatPercentage(data?.financeConditionItem?.apr || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>First Date</DealBuilderLabel> {formatDate(data?.financeConditionItem?.firstPaymentDate)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>First Payment</DealBuilderLabel>{' '}
          {formatCurrency(data?.financeConditionItem?.monthlyPaymentAmount || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Last Payment</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.lastPaymentAmount || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>Down Payment</DealBuilderLabel> {formatCurrency(data?.financeConditionItem?.cashDownAmount || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>PTI</DealBuilderLabel> {formatPercentage(data?.financeConditionItem?.pti || 0.0)}
        </DealBuilderBodySection>
        <DealBuilderBodySection>
          <DealBuilderLabel>New DTI</DealBuilderLabel> {formatPercentage(data?.financeConditionItem?.newDti || 0.0)}
        </DealBuilderBodySection>
      </DealBuilderBody>
    </div>
  );
};
